import getItemFromLocalStorage from './getItemFromLocalStorage';

export interface AuthUser {
  pk: number;
  name: string;
  email: string;
  access: string;
  refresh: string;
  profile?: string;
  is_super_host?: boolean;
}

export interface AuthState {
  isAuthenticated: boolean;
  user: AuthUser | null;
  isLoading: boolean;
}

/**
 * Validates if a user object has all required fields
 */
export const isValidUser = (user: any): user is AuthUser => {
  return (
    user &&
    typeof user === 'object' &&
    typeof user.pk === 'number' &&
    typeof user.name === 'string' &&
    typeof user.email === 'string' &&
    typeof user.access === 'string' &&
    typeof user.refresh === 'string'
  );
};

/**
 * Gets the current authenticated user from localStorage
 */
export const getCurrentUser = (): AuthUser | null => {
  const user = getItemFromLocalStorage('user');
  return isValidUser(user) ? user : null;
};

/**
 * Checks if the user is currently authenticated
 */
export const isAuthenticated = (): boolean => {
  const user = getCurrentUser();
  return user !== null;
};

/**
 * Clears all authentication data
 */
export const clearAuth = (): void => {
  localStorage.clear();
  console.log('Authentication data cleared');
};

/**
 * Dispatches token expiration event
 */
export const dispatchTokenExpired = (): void => {
  window.dispatchEvent(new CustomEvent('tokenExpired'));
};

/**
 * Dispatches authentication required event
 */
export const dispatchAuthRequired = (): void => {
  window.dispatchEvent(new CustomEvent('authRequired'));
};

/**
 * Handles authentication errors (401, token expiration, etc.)
 */
export const handleAuthError = (error: any): void => {
  console.error('Authentication error:', error);
  
  if (error.response?.status === 401) {
    clearAuth();
    dispatchTokenExpired();
    dispatchAuthRequired();
  }
};

/**
 * Hook for listening to authentication events
 */
export const useAuthEvents = (onTokenExpired: () => void, onAuthRequired: () => void) => {
  const addEventListeners = () => {
    window.addEventListener('tokenExpired', onTokenExpired);
    window.addEventListener('authRequired', onAuthRequired);
  };

  const removeEventListeners = () => {
    window.removeEventListener('tokenExpired', onTokenExpired);
    window.removeEventListener('authRequired', onAuthRequired);
  };

  return { addEventListeners, removeEventListeners };
};

/**
 * Validates current user from localStorage before Firebase operations
 * Only checks essential fields (pk, name, email) without requiring tokens
 * Throws error if user data is invalid or expired
 */
export const validateCurrentUserForFirebase = (): void => {
  const user = getItemFromLocalStorage('user');

  const throwInvalidUserError = () => {
    throw new Error('INVALID_USER_DATA');
  };

  // Must be an object
  if (!user || typeof user !== 'object') {
    throwInvalidUserError();
  }

  // pk must exist and be non-empty when stringified (accept number or string)
  const pkStr = String((user as any).pk ?? '').trim();
  if (pkStr.length === 0) {
    throwInvalidUserError();
  }

  // name must be a non-empty string
  if (typeof (user as any).name !== 'string' || (user as any).name.trim().length === 0) {
    throwInvalidUserError();
  }

  // email must be a non-empty string
  if (typeof (user as any).email !== 'string' || (user as any).email.trim().length === 0) {
    throwInvalidUserError();
  }
};